<template>
  <BaseCard title="Discount Code">
    <BaseFormErrorSummary
      :errors="errors"
      :submit-count="submitCount"
    />

    <BaseInlineNotification
      v-if="submissionError"
      heading-text="An Error Occurred"
      hide-title-element
      type="error"
    >
      <p
        class="body-sm"
        v-text="submissionError"
      />
    </BaseInlineNotification>

    <BaseInlineNotification
      v-if="successMessage"
      heading-text="Success"
      hide-title-element
      type="success"
    >
      <p
        class="body-sm"
        v-text="successMessage"
      />
    </BaseInlineNotification>

    <BaseInlineNotification
      v-if="infoMessage"
      heading-text="Success"
      hide-title-element
      type="info"
    >
      <p
        class="body-sm"
        v-text="infoMessage"
      />
    </BaseInlineNotification>

    <!-- If a discount is applied, show the applied code and remove button -->
    <div v-if="hasDiscount" class="flex items-end gap-x-2">
      <BaseTextInput
        id="code-applied"
        :model-value="appliedCodeText"
        disabled
        label="Applied Code"
        name="code-applied"
      />
      <button
        :disabled="isRemoving"
        class="btn btn--outline"
        data-test-id="remove-coupon-button"
        type="button"
        @click="handleRemoveCouponCode"
      >
        <span class="relative inline-flex items-center justify-center">
          <BaseLoadingIcon :visible="isRemoving" />
          <span class="whitespace-nowrap" :class="{ 'invisible': isRemoving }">Remove</span>
        </span>
      </button>
    </div>

    <!-- If no discount is applied, show apply button -->
    <div v-else class="flex items-end gap-x-2">
      <BaseTextInput
        id="code"
        v-model="values.code"
        label="Group Code"
        name="code"
        @keydown.enter.stop="handleApplyCouponCode"
      />
      <button
        :disabled="isLoading"
        class="btn btn--outline"
        data-test-id="apply-coupon-button"
        type="button"
        @click="handleApplyCouponCode"
      >
        <span class="relative inline-flex items-center justify-center">
          <BaseLoadingIcon :visible="isLoading" />
          <span
            class="whitespace-nowrap"
            :class="{ 'invisible': isLoading }"
            v-text="'Apply'"
          />
        </span>
      </button>
    </div>
  </BaseCard>
</template>

<script lang="ts" setup>
import { ref, computed, watch } from 'vue'
import { useForm } from 'vee-validate'
import { log } from '@js/utils/logger'
import { applyCouponToCart } from '@js/api/cart/applyCouponToCart'
import { removeCouponFromCart } from '@js/api/cart/removeCouponFromCart'
import { Cart } from '@js/api/types/cart/Cart'
import { applyCouponToRegistration } from '@js/api/checkout/ApplyCouponToRegistration'

import BaseCard from '@js/components/global/BaseCard.vue'
import BaseTextInput from '@js/components/global/forms/inputs/BaseTextInput.vue'
import BaseInlineNotification from '@js/components/global/BaseInlineNotification.vue'
import BaseFormErrorSummary from '@js/components/global/forms/BaseFormErrorSummary.vue'
import useCart from '@js/composables/useCart'
import BaseLoadingIcon from '@js/components/global/BaseLoadingIcon.vue'

const props = defineProps<{
  cart: Cart | null
  registrationId: number | null
}>()

const emit = defineEmits<{
  cartUpdated: [cart: Cart | null]
}>()

const { setCartInStorage } = useCart()

const {
  errors,
  values,
  resetForm,
  submitCount,
  handleSubmit: handleValidationSubmit,
} = useForm({
  initialValues: {
    code: '',
  },
})

const isLoading = ref(false)
const isRemoving = ref(false)
const successMessage = ref('')
const infoMessage = ref('')
const submissionError = ref('')
const localCart = ref<Cart | null>(props.cart)

const pickCouponCode = (cart) => cart?.coupon_details?.code?.trim() ?? ''

/**
 * Handles determining if the cart has a coupon code applied.
 * Only returns true when an actual coupon code is set on the cart,
 * not for admin-applied automatic discounts (cart price rules with no coupon code).
 */
const hasDiscount = computed(() => {
  const cartAny = localCart.value as any
  const hasCouponCode = Boolean(cartAny?.coupon_details?.code?.trim())
  return hasCouponCode && (Boolean(cartAny?.prices?.discount) || Boolean(cartAny?.applied_coupons?.length))
})

/**
 * True when a global/automatic discount is active on the cart (admin-created cart price
 * rule with no coupon code), indicated by a discount amount present but no coupon code.
 * These rules may have "Stop Further Rules Processing" enabled in Magento, which silently
 * blocks any manually entered coupon from producing an additional discount.
 */
const hasGlobalAutoDiscount = computed(() => {
  const cartAny = localCart.value as any
  const hasCouponCode = Boolean(cartAny?.coupon_details?.code?.trim())
  return !hasCouponCode && Boolean(cartAny?.prices?.discount)
})

/**
 * Handles setting info message based on the cart's coupon code or discount status.
 */
const setInfoMsgFromCart = (cart: Cart | null) => {
  const code = pickCouponCode(cart as any)

  if (code) {
    infoMessage.value = code
  } else {
    // No coupon code on the cart — could be an auto-applied admin discount.
    // Don't show a confusing "code applied" message in this case.
    infoMessage.value = ''
  }
}

/**
 * Handles generating the coupon code text to display when a discount is applied.
 */
const appliedCodeText = computed(() => {
  const cartAny = localCart.value as any
  if (!cartAny) return ''

  const couponName = cartAny?.coupon_details?.rule_name?.trim()
  const couponCode = cartAny?.coupon_details?.code?.trim()

  return couponName || couponCode || ''
})

/**
 * Handles applying a coupon code to the cart.
 */
const handleApplyCouponCode = handleValidationSubmit(async (): Promise<void> => {
  isLoading.value = true
  if (!props.cart?.id) return

  try {
    submissionError.value = ''

    // Capture the discount amount before applying so we can detect whether
    // a "Stop Further Rules Processing" global rule silently blocked the coupon.
    const preApplyDiscountValue = (localCart.value as any)?.prices?.discount?.amount?.value ?? null

    const updatedCart = await applyCouponToCart({
      cartId: props.cart?.id ?? '',
      couponCode: values.code,
    })

    if (updatedCart) {
      const postApplyDiscountValue = (updatedCart as any)?.prices?.discount?.amount?.value ?? null

      // If a global auto-discount was active and the discount amount did not change
      // after applying the coupon, Magento's "Stop Further Rules Processing" flag on
      // the global discount rule has blocked this coupon from providing any reduction.
      const globalDiscountBlockedCoupon =
        hasGlobalAutoDiscount.value &&
        preApplyDiscountValue !== null &&
        postApplyDiscountValue !== null &&
        Math.abs(preApplyDiscountValue) === Math.abs(postApplyDiscountValue)

      if (globalDiscountBlockedCoupon) {
        // The coupon was accepted by Magento but had no effect because a global
        // "Stop Further Rules Processing" discount is active. Roll it back so
        // that on page reload the cart is clean and the field stays empty.
        try {
          const rolledBackCart = await removeCouponFromCart({ cartId: props.cart?.id ?? '' })
          if (rolledBackCart) {
            setCartInStorage(rolledBackCart)
            emit('cartUpdated', rolledBackCart)
            localCart.value = rolledBackCart
          }
        } catch {
          // rollback best-effort — ignore secondary error
        }

        submissionError.value = `The coupon code "${values.code}" is not valid.`
        log({
          message: 'Global Discount Blocked Coupon',
          file: 'src/wcei/js/components/checkout/partials/CouponCard.vue',
        })
        return
      }

      setCartInStorage(updatedCart)
      emit('cartUpdated', updatedCart)
      localCart.value = updatedCart

      const couponCode = (updatedCart as any)?.coupon_details?.code || values.code
      infoMessage.value = couponCode || 'Group, discount, or payment code was applied successfully.'

      successMessage.value = ''

      await applyCouponToRegistration({
        cartId: props.cart.id,
        couponCode: couponCode || '',
        registrationId: props.registrationId ?? 0,
      })
    }
  } catch (error) {
    const errorMessage = error as string

    submissionError.value = errorMessage.toLowerCase().includes('verify') || errorMessage.toLowerCase().includes('global discount')
      ? errorMessage
      : 'An error occurred while applying the coupon code.'

    log({
      message: 'Apply Coupon Error',
      file: 'src/wcei/js/components/checkout/partials/CouponCard.vue',
    })
  } finally {
    if (submissionError.value) {
      resetForm()
    }
    isLoading.value = false
  }
})

/**
 * Handles removing a coupon code from the cart.
 */
const handleRemoveCouponCode = async (): Promise<void> => {
  if (!props.cart?.id) return

  isRemoving.value = true
  successMessage.value = ''

  try {
    submissionError.value = ''
    const updatedCart = await removeCouponFromCart({ cartId: props.cart.id })

    if (updatedCart) {
      setCartInStorage(updatedCart)
      emit('cartUpdated', updatedCart)
      localCart.value = updatedCart
      successMessage.value = 'Code removed successfully.'
      infoMessage.value = ''

      const couponCode = (updatedCart as any)?.coupon_details?.code || values.code

      await applyCouponToRegistration({
        cartId: props.cart.id,
        couponCode: couponCode || '',
        registrationId: props.registrationId ?? 0,
      })
    }
  } catch (error) {
    const errorMessage = String(error ?? '')

    submissionError.value = errorMessage.toLowerCase().includes('verify')
      ? errorMessage
      : 'An error occurred while removing the coupon code.'

    log({
      message: 'Remove Coupon Error',
      file: 'src/wcei/js/components/checkout/partials/CouponCard.vue',
    })
  } finally {
    resetForm()
    isRemoving.value = false
  }
}


watch(() => props.cart, (c) => setInfoMsgFromCart(c), { immediate: true })

watch(() => props.cart, (newCart) => {
  localCart.value = newCart
})
</script>
